import { computed, type ComputedRef, type Ref } from 'vue'
import type { JewelleriesProps } from './Jewelleries.vue'
import {
    type GetJewelleriesRouteParam,
    type RouteParamsTH,
    useRoutes,
} from '@/router/routes'
import type {
    CatalogueSortOrder,
    JewelleryBraceletProductStyle,
    JewelleryDesignFeature,
    JewelleryDesignStyle,
    JewelleryEarringsProductStyle,
    JewelleryGemstoneCode,
    JewelleryNecklaceProductStyle,
    MetalCode,
} from '@/graphql/api'
import { type JewelleryProductType, type ROUTE_JEWELLERIES } from '@/router'
import { useFiltersCount } from '../useFiltersCount'
import type { FilterChip } from '@/components/ui/filter/FilterChips.vue'
import type { JewelleryFilters } from './useJewelleryData'
import { getJewelleryProductTypeLabel } from './getJewelleryProductTypeLabel'
import { useFormattedPriceRange } from '@/composables/useMoneyFormatter'
import { systemCodeToWords } from '@/helpers'
import type { InputRangeModel } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'

export interface JewelleriesFilters {
    filterRoutes: {
        routeReset: RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRoutePrice(
            values: InputRangeModel
        ): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteAddMetal(
            metal: MetalCode
        ): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteRemoveMetal(): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteAddJewelleryType(
            productType: JewelleryProductType
        ): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteRemoveJewelleryType(): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteAddProductStyle(
            productStyle:
                | JewelleryBraceletProductStyle
                | JewelleryNecklaceProductStyle
                | JewelleryEarringsProductStyle
        ): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteRemoveProductStyle(): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteAddGemstone(
            gemstone: JewelleryGemstoneCode
        ): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteRemoveGemstone(): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteAddDesignStyle(
            designStyle: JewelleryDesignStyle
        ): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteRemoveDesignStyle(): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteAddDesignFeature(
            designFeature: JewelleryDesignFeature
        ): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteRemoveDesignFeature(
            designFeature: JewelleryDesignFeature
        ): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRoutePage(page: number): RouteParamsTH[typeof ROUTE_JEWELLERIES]

        getRouteSort(
            sortBy: CatalogueSortOrder
        ): RouteParamsTH[typeof ROUTE_JEWELLERIES]
    }
    activeFilterCount: ComputedRef<number>
    filterChips: ComputedRef<FilterChip[]>
}

export function useJewelleriesFilters(
    props: JewelleriesProps,
    filters: Readonly<Ref<JewelleryFilters | undefined>>
): JewelleriesFilters {
    const routes = useRoutes()

    const getRoute = (
        values: GetJewelleriesRouteParam
    ): RouteParamsTH[typeof ROUTE_JEWELLERIES] => {
        const current = {
            productStyle: props.productStyle,
            gemstone: props.gemstone,
            productType: props.productType,
            metal: props.metal?.[0],
            priceMin: props.priceMin,
            priceMax: props.priceMax,
            designStyle: props.designStyle,
            designFeatures: props.designFeatures,
            sortBy: props.sortBy,
        }

        return routes.jewelleries({
            ...current,
            ...values,
        })
    }

    const filterRoutes = {
        routeReset: routes.jewelleries({}),

        getRoutePrice: (values: InputRangeModel) =>
            getRoute({
                priceMin: Number(values[0]),
                priceMax: Number(values[1]),
            }),

        getRouteAddMetal: (metal: MetalCode) => getRoute({ metal }),

        getRouteRemoveMetal: () => getRoute({ metal: undefined }),

        getRouteAddJewelleryType: (productType: JewelleryProductType) =>
            getRoute({ productType, productStyle: undefined }),

        getRouteRemoveJewelleryType: () =>
            getRoute({ productType: undefined, productStyle: undefined }),

        getRouteAddProductStyle: (
            productStyle:
                | JewelleryBraceletProductStyle
                | JewelleryNecklaceProductStyle
                | JewelleryEarringsProductStyle
        ) => getRoute({ productStyle }),

        getRouteRemoveProductStyle: () => getRoute({ productStyle: undefined }),

        getRouteAddGemstone: (gemstone: JewelleryGemstoneCode) =>
            getRoute({ gemstone }),

        getRouteRemoveGemstone: () => getRoute({ gemstone: undefined }),

        getRouteAddDesignStyle: (designStyle: JewelleryDesignStyle) =>
            getRoute({ designStyle }),

        getRouteRemoveDesignStyle: () => getRoute({ designStyle: undefined }),

        getRouteAddDesignFeature: (designFeature: JewelleryDesignFeature) => {
            const designFeatures = props.designFeatures
                ? [...props.designFeatures, designFeature]
                : [designFeature]

            return getRoute({ designFeatures })
        },

        getRouteRemoveDesignFeature: (
            designFeature: JewelleryDesignFeature
        ) => {
            const designFeatures = props.designFeatures?.filter(
                (feature) => feature !== designFeature
            )

            return getRoute({ designFeatures })
        },

        getRoutePage: (page: number) => getRoute({ page }),

        getRouteSort: (sortBy: CatalogueSortOrder) =>
            getRoute({ sortBy, page: 1 }),
    }

    const activeFilterCount = useFiltersCount(
        computed(() => [
            !!props.productType,
            !!props.metal?.length,
            !!props.designStyle,
            !!props.productStyle,
            !!props.gemstone,
            !!props.designFeatures?.length,
            !!(props.priceMin || props.priceMax),
        ])
    )

    const formattedPriceRange = useFormattedPriceRange(
        computed(() => ({
            min: { amount: props.priceMin },
            max: { amount: props.priceMax },
        }))
    )

    const filterChips = computed(() => {
        const chips: FilterChip[] = []

        if (formattedPriceRange.value) {
            chips.push({
                label: formattedPriceRange.value,
                getRouteRemove: () =>
                    getRoute({
                        priceMin: undefined,
                        priceMax: undefined,
                    }),
            })
        }

        if (props.productType) {
            chips.push({
                label: getJewelleryProductTypeLabel(props.productType)
                    .translation.name,
                getRouteRemove: filterRoutes.getRouteRemoveJewelleryType,
            })
        }

        const firstMetalCode = props.metal?.[0]
        const selectedMetalsCount = props.metal?.length || 0
        if (firstMetalCode) {
            const metal = filters.value?.available.primaryMetals.find(
                (m) => m.metal.systemCode === firstMetalCode
            )?.metal

            if (selectedMetalsCount > 1) {
                chips.push({
                    label: metal
                        ? `${metal.color?.translation.name} ${metal.element.translation.name}`
                        : systemCodeToWords(firstMetalCode),
                    getRouteRemove: filterRoutes.getRouteRemoveMetal,
                })
            } else {
                chips.push({
                    label:
                        metal?.translation.name ||
                        systemCodeToWords(firstMetalCode),
                    getRouteRemove: filterRoutes.getRouteRemoveMetal,
                })
            }
        }

        if (
            filters.value?.available &&
            props.productStyle &&
            'productStyles' in filters.value.available
        ) {
            chips.push({
                label:
                    filters.value?.available.productStyles.find(
                        (p) => p.label.systemCode === props.productStyle
                    )?.label.translation.name ||
                    systemCodeToWords(props.productStyle),
                getRouteRemove: filterRoutes.getRouteRemoveProductStyle,
            })
        }

        if (props.gemstone) {
            chips.push({
                label:
                    filters.value?.available.gemstones.find(
                        (g) => g.label.systemCode === props.gemstone
                    )?.label.translation.name ||
                    systemCodeToWords(props.gemstone),
                getRouteRemove: filterRoutes.getRouteRemoveGemstone,
            })
        }

        if (props.designStyle) {
            chips.push({
                label:
                    filters.value?.available.designStyles.find(
                        (d) => d.label.systemCode === props.designStyle
                    )?.label.translation.name ||
                    systemCodeToWords(props.designStyle),
                getRouteRemove: filterRoutes.getRouteRemoveDesignStyle,
            })
        }

        return chips
    })

    return {
        filterRoutes,
        activeFilterCount,
        filterChips,
    }
}
